<template>
  <div class="deal-list">
    <div v-for="stage in stages" :key="stage.id" class="deal-list__stage">
      <button class="deal-list__stage-header" @click="toggleStage(stage.id)">
        <div class="deal-list__stage-left">
          <span class="deal-list__stage-dot" :style="{ backgroundColor: stage.color }"></span>
          <span class="deal-list__stage-name">{{ stage.name }}</span>
        </div>
        <div class="deal-list__stage-right">
          <span class="deal-list__stage-value">
            {{ formatCurrency(stage.total_value) }}
          </span>
          <span
            class="deal-list__stage-count"
            :style="{ backgroundColor: stageColor(stage), color: stageTextColor(stage) }"
          >
            {{ stage.deals_count || stage.deals?.length || 0 }}
          </span>
          <BaseIcon
            name="chevron"
            size="14"
            class="deal-list__stage-chevron"
            :class="{ 'deal-list__stage-chevron--open': openStages.includes(stage.id) }"
          />
        </div>
      </button>

      <Transition name="accordion">
        <div v-if="openStages.includes(stage.id)" class="deal-list__stage-body">
          <DealCard
            v-for="deal in stage.deals"
            :key="deal.id"
            :deal="deal"
            :compact="true"
            :selected="selectedDealIds.includes(deal.id)"
            @click="$emit('card-click', deal)"
            @select="(event) => $emit('card-select', deal.id, event)"
          />
          <div class="deal-list__empty" v-if="!stage.deals || stage.deals.length === 0">
            {{ $t('deals.no_deals_in_stage') }}
          </div>
        </div>
      </Transition>
    </div>

    <div class="deal-list__stage deal-list__stage--archive">
      <button
        type="button"
        class="deal-list__stage-header"
        :aria-expanded="archiveExpanded"
        @click="toggleArchive"
      >
        <div class="deal-list__stage-left">
          <span class="deal-list__stage-dot deal-list__stage-dot--archive"></span>
          <span class="deal-list__stage-name">{{ $t('deals.archive_column') }}</span>
        </div>
        <div class="deal-list__stage-right">
          <span class="deal-list__stage-value">
            {{ formatCurrency(archiveTotal) }}
          </span>
          <span class="deal-list__stage-count deal-list__stage-count--archive">
            {{ archiveCount }}
          </span>
          <BaseIcon
            name="chevron"
            size="14"
            class="deal-list__stage-chevron"
            :class="{ 'deal-list__stage-chevron--open': archiveExpanded }"
          />
        </div>
      </button>

      <Transition name="accordion">
        <div v-if="archiveExpanded" class="deal-list__stage-body">
          <DealCard
            v-for="deal in archiveDeals"
            :key="deal.id"
            :deal="deal"
            :compact="true"
            :selected="selectedDealIds.includes(deal.id)"
            @click="$emit('card-click', deal)"
            @select="(event) => $emit('card-select', deal.id, event)"
          />
          <div class="deal-list__empty" v-if="archiveCount === 0">
            {{ $t('deals.archive_empty') }}
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
  import { computed, ref, getCurrentInstance } from 'vue';
  import DealCard from '@/components/deals/DealCard.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { readableTextColor } from '@shared/utils/colorContrast';

  export default {
    name: 'DealListView',

    components: {
      DealCard,
      BaseIcon,
    },

    props: {
      stages: {
        type: Array,
        required: true,
        default: () => [],
      },
      selectedDealIds: {
        type: Array,
        default: () => [],
      },
      archive: {
        type: Object,
        default: () => ({ deals: [], deals_count: 0, total_value: 0 }),
      },
      archiveExpanded: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['card-click', 'card-select', 'update:archiveExpanded'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const DEFAULT_STAGE_COLOR = '#434f6f';

      const stageColor = (stage) => stage.color || DEFAULT_STAGE_COLOR;

      // Stage colours are user-picked, so white text is not always readable
      const stageTextColor = (stage) => readableTextColor(stageColor(stage), { dark: '#18181b' });

      const openStages = ref([]);

      const archiveDeals = computed(() => props.archive?.deals || []);
      const archiveCount = computed(() => archiveDeals.value.length);
      const archiveTotal = computed(() =>
        archiveDeals.value.reduce((sum, deal) => sum + (Number(deal.value) || 0), 0)
      );

      const toggleStage = (stageId) => {
        const idx = openStages.value.indexOf(stageId);
        if (idx === -1) {
          openStages.value.push(stageId);
        } else {
          openStages.value.splice(idx, 1);
        }
      };

      const toggleArchive = () => {
        emit('update:archiveExpanded', !props.archiveExpanded);
      };

      const formatCurrency = (value) => {
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);
      };

      return {
        openStages,
        toggleStage,
        archiveDeals,
        archiveCount,
        archiveTotal,
        toggleArchive,
        formatCurrency,
        stageColor,
        stageTextColor,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &__stage {
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      border-radius: 0.75rem;
      overflow: hidden;

      &--archive {
        background: #e6e8ee;
      }
    }

    &__stage-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 0.875rem 1rem;
      background: none;
      border: none;
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background: var(--slate-100);
      }
    }

    &__stage-left {
      display: flex;
      align-items: center;
      gap: 0.625rem;
    }

    &__stage-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;

      &--archive {
        background: #434f6f;
      }
    }

    &__stage-name {
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__stage-right {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &__stage-value {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-500);
    }

    &__stage-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 16px;
      padding: 4px;
      border-radius: 6px;
      font-size: 10px;
      font-weight: 300;
      line-height: 1;

      &--archive {
        background: #434f6f;
        color: #fff;
      }
    }

    &__stage-chevron {
      color: var(--slate-400);
      transition: transform 0.2s ease;
      transform: rotate(90deg);

      &--open {
        transform: rotate(-90deg);
      }
    }

    &__stage-body {
      border-top: 1px solid var(--slate-200);
      background: white;
    }

    &__empty {
      padding: 1.5rem;
      text-align: center;
      font-size: 0.8125rem;
      color: var(--slate-400);
    }
  }

  .accordion-enter-active,
  .accordion-leave-active {
    transition: all 0.2s ease;
    overflow: hidden;
  }

  .accordion-enter-from,
  .accordion-leave-to {
    opacity: 0;
    max-height: 0;
  }

  .accordion-enter-to,
  .accordion-leave-from {
    opacity: 1;
    max-height: 2000px;
  }
</style>
